import React, { useState } from 'react';
import { ChevronDownIcon, ClipboardDocumentIcon, SpeakerWaveIcon } from '@heroicons/react/24/outline';
import DailyPracticeRadar from './DailyPracticeRadar';
import { practiceStageLabel, visiblePracticeStages } from '../lib/dailyPracticeReport';
import { getTierInfo } from '../lib/learningTiers';
import { useI18n } from '../i18n/I18nProvider';

const localDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DailyPracticeReportPanel = ({
  report,
  date,
  onDateChange,
  loading,
  error,
  studentName,
  sessions,
  expandedSession,
  onExpandedSessionChange,
  onCopyRaw,
  copyState,
}) => {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [recordingState, setRecordingState] = useState({ id: '', error: '' });
  const metrics = report?.student;
  const totalAttempts = visiblePracticeStages(report).reduce(
    (total, stage) => total + Number(metrics?.stages?.[stage.key]?.attempts || 0),
    0
  );
  const percentage = (value) => value == null ? t('Not recorded') : `${Number(value).toFixed(1)}%`;
  const visibleSessions = (Array.isArray(sessions) ? sessions : []).filter((session) => {
    const attempts = Number(session.attempts_total || 0);
    const pretest = Number(session.pretest_total || 0);
    const events = Array.isArray(session.events) ? session.events : [];
    const isEmptyStart = session.status === 'started' && attempts === 0 && pretest === 0 && events.length === 0;
    const occursOnDate = [session.started_at, session.last_activity_at, session.completed_at].some(value => localDate(value) === date)
      || events.some(event => localDate(event.ts) === date);
    return !isEmptyStart && occursOnDate;
  });

  const playPronunciationRecording = async (recordingId) => {
    if (!recordingId || !report?.student_id || recordingState.id) return;
    setRecordingState({ id: recordingId, error: '' });
    try {
      const response = await fetch(
        `/api/admin/students/${encodeURIComponent(report.student_id)}/pronunciation-recordings/${encodeURIComponent(recordingId)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      if (!response.ok) throw new Error(t('This recording could not be played.'));
      const blobUrl = URL.createObjectURL(await response.blob());
      const audio = new Audio(blobUrl);
      const release = () => {
        URL.revokeObjectURL(blobUrl);
        setRecordingState({ id: '', error: '' });
      };
      audio.addEventListener('ended', release, { once: true });
      audio.addEventListener('error', release, { once: true });
      await audio.play();
    } catch (playError) {
      setRecordingState({ id: '', error: playError.message || t('This recording could not be played.') });
    }
  };

  return (
    <section className="bg-white p-5 sm:p-6 rounded-lg shadow border border-gray-100" aria-labelledby="daily-practice-title">
      <header className={`flex flex-wrap items-center justify-between gap-4 ${expanded ? 'border-b border-gray-200 pb-4' : ''}`}>
        <div>
          <h4 id="daily-practice-title" className="text-lg font-bold text-gray-950">{t('Daily Practice Report')}</h4>
          <p className="mt-1 text-sm text-gray-500">
            {expanded
              ? t('Pre-test, four-step practice, completed vocabulary, and class comparison.')
              : t('{attempts} attempts, {learned} learned, {reviewed} reviewed', {
                  attempts: totalAttempts,
                  learned: metrics?.learned_total || 0,
                  reviewed: metrics?.reviewed_total || 0,
                })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-medium text-gray-700">
            {t('Report date')}
            <input
              type="date"
              value={date}
              onChange={(event) => onDateChange(event.target.value)}
              className="ml-3 rounded border border-gray-300 bg-white px-3 py-2 text-gray-900"
            />
          </label>
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls="daily-practice-content"
            onClick={() => setExpanded(value => !value)}
            className="inline-flex h-10 w-10 items-center justify-center rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            title={expanded ? t('Collapse report') : t('Expand report')}
          >
            <ChevronDownIcon className={`h-5 w-5 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
        </div>
      </header>

      <div id="daily-practice-content">
      {expanded && loading && <div className="py-12 text-center text-gray-500">{t('Building daily report...')}</div>}
      {expanded && error && <div role="alert" className="mt-4 border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {expanded && !loading && !error && metrics && (
        <>
          <div className="grid border-b border-gray-200 md:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)]">
            <div className="py-5 md:pr-6">
              <div className="grid grid-cols-2 divide-x divide-gray-200 border-y border-gray-200 sm:grid-cols-4">
                <div className="px-3 py-4">
                  <div className="text-xs font-semibold uppercase text-gray-500">Pre-test passed</div>
                  <div className="mt-1 text-2xl font-bold text-gray-950">
                    {metrics.pretest?.recorded ? `${metrics.pretest.passed}/${metrics.pretest.attempted}` : t('Not recorded')}
                  </div>
                </div>
                <div className="px-3 py-4">
                  <div className="text-xs font-semibold uppercase text-gray-500">Words learned</div>
                  <div className="mt-1 text-2xl font-bold text-blue-700">{metrics.learned_total || 0}</div>
                </div>
                <div className="px-3 py-4">
                  <div className="text-xs font-semibold uppercase text-gray-500">Words reviewed</div>
                  <div className="mt-1 text-2xl font-bold text-emerald-700">{metrics.reviewed_total || 0}</div>
                </div>
                <div className="px-3 py-4">
                  <div className="text-xs font-semibold uppercase text-gray-500">Average level</div>
                  <div className="mt-1 text-2xl font-bold text-violet-700">
                    {metrics.average_tier == null ? 'No data' : `Tier ${metrics.average_tier}`}
                  </div>
                  {metrics.average_tier_word_count > 0 && <div className="mt-1 text-xs text-gray-500">{metrics.average_tier_word_count} completed words</div>}
                </div>
              </div>

              <div className="mt-5 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b text-xs uppercase text-gray-500">
                    <tr><th className="py-2 pr-3">Practice mode</th><th className="p-2">Attempts</th><th className="p-2">Correct</th><th className="p-2">Accuracy</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visiblePracticeStages(report).map((stage) => {
                      const item = metrics.stages?.[stage.key] || {};
                      return (
                        <tr key={stage.key}>
                          <th className="py-3 pr-3 font-medium text-gray-900">{t(stage.label)}</th>
                          <td className="p-2 tabular-nums">{item.attempts || 0}</td>
                          <td className="p-2 tabular-nums">{item.correct || 0}</td>
                          <td className="p-2 font-medium tabular-nums">{percentage(item.accuracy)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
                <div className="text-sm text-gray-500">
                  {t('Compared with {className} ({count} students).', {
                    className: report?.cohort?.class_name || t('the class'),
                    count: report?.cohort?.student_count || 0,
                  })}
                </div>
                <details className="relative">
                  <summary className="cursor-pointer list-none rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                    Advanced
                  </summary>
                  <div className="absolute right-0 z-20 mt-2 w-72 border border-gray-200 bg-white p-4 shadow-lg">
                    <p className="text-sm leading-relaxed text-gray-600">Copy every practice record for this student on the selected date as structured data.</p>
                    <button
                      type="button"
                      onClick={onCopyRaw}
                      disabled={copyState === 'copying'}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      {copyState === 'copying' ? 'Preparing logs...' : copyState === 'copied' ? 'Copied' : 'Copy raw daily logs'}
                    </button>
                  </div>
                </details>
              </div>
            </div>
            <div className="min-w-0 py-5 md:border-l md:border-gray-200 md:pl-6">
              <DailyPracticeRadar report={report} studentName={studentName} />
            </div>
          </div>

          <div className="pt-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h5 className="font-semibold text-gray-900">Session details</h5>
                <p className="text-sm text-gray-500">Only sessions with recorded activity are shown.</p>
              </div>
              <span className="text-sm text-gray-500">{t('{count} active sessions', { count: visibleSessions.length })}</span>
            </div>
            {visibleSessions.length > 0 ? (
              <div className="divide-y border-y border-gray-200">
                {visibleSessions.map((session) => {
                  const expanded = expandedSession === session.session_id;
                  const events = (session.events || []).filter(event => localDate(event.ts) === date);
                  const attempts = Number(session.attempts_total || 0);
                  const correct = Number(session.correct_attempts || 0);
                  return (
                    <div key={session.session_id}>
                      <button
                        type="button"
                        onClick={() => onExpandedSessionChange(expanded ? '' : session.session_id)}
                        className="w-full px-1 py-4 text-left hover:bg-gray-50"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <div className="font-semibold text-gray-900">{t(session.mode === 'review' ? 'Review' : 'Learning')} · {t(getTierInfo(session.tier).studentLabel)}</div>
                            <div className="mt-1 text-xs text-gray-500">{session.started_at ? new Date(session.started_at).toLocaleString() : 'Unknown time'}</div>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs">
                            {session.status === 'completed' && <span className="bg-green-100 px-2 py-1 font-medium text-green-800">Completed</span>}
                            {Number(session.pretest_total || 0) > 0 && <span className="bg-blue-50 px-2 py-1 text-blue-800">Pre-test {session.pretest_passed || 0}/{session.pretest_total}</span>}
                            {attempts > 0 && <span className="bg-gray-100 px-2 py-1 text-gray-700">{t('{count} attempts · {accuracy}%', { count: attempts, accuracy: Math.round(correct / attempts * 100) })}</span>}
                          </div>
                        </div>
                      </button>
                      {expanded && (
                        <div className="bg-gray-50 px-4 py-4">
                          <div className="mb-4 flex flex-wrap gap-2">
                            {(session.items || []).map((item) => (
                              <span key={`${item.word}::${item.sense_id}`} data-i18n-skip className="border bg-white px-2 py-1 text-xs text-gray-700">
                                <strong>{item.word}</strong>{item.definition_cn ? ` · ${item.definition_cn}` : ''}
                              </span>
                            ))}
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-left text-sm">
                              <thead className="text-xs uppercase text-gray-500"><tr><th className="p-2">Time</th><th className="p-2">Word</th><th className="p-2">Mode</th><th className="p-2">Result</th></tr></thead>
                              <tbody className="divide-y bg-white">
                                {events.map((event, index) => {
                                  const isAiHint = event.event === 'reordering_ai_hint';
                                  const isPronunciation = event.pronunciation_score !== undefined;
                                  const stageLabel = practiceStageLabel(event, session);
                                  return (
                                    <tr key={`${event.ts || index}-${index}`}>
                                      <td className="p-2 whitespace-nowrap text-xs text-gray-500">{event.ts ? new Date(event.ts).toLocaleTimeString() : ''}</td>
                                      <td data-i18n-skip className="p-2 font-medium">{event.word}</td>
                                      <td className="p-2">
                                        {isAiHint
                                          ? t('AI sentence hint')
                                          : event.event === 'pretest_attempt'
                                            ? t('Pre-test')
                                            : t(stageLabel)}
                                        {event.spelling_correct !== undefined && (
                                          <div className="mt-1 text-xs text-gray-500">
                                            {t('Spelling')}: {t(event.spelling_correct ? 'Correct' : 'Retry')}
                                          </div>
                                        )}
                                        {event.meaning_correct !== undefined && (
                                          <div className="text-xs text-gray-500">
                                            {t('Meaning')}: {t(event.meaning_correct ? 'Correct' : 'Retry')}
                                          </div>
                                        )}
                                      </td>
                                      <td className={`p-2 ${isAiHint ? 'text-gray-700' : `font-medium ${event.correct ? 'text-green-700' : 'text-red-700'}`}`}>
                                        {isPronunciation ? (
                                          <div className="min-w-44 space-y-2">
                                            <div className="flex flex-wrap items-center gap-2">
                                              <span className={`rounded px-2 py-1 text-sm font-bold ${event.correct ? 'bg-green-50 text-green-800' : 'bg-rose-50 text-rose-800'}`}>
                                                {Number(event.pronunciation_score).toFixed(0)}/100
                                              </span>
                                              <span>{t(event.correct ? 'Correct' : 'Retry')}</span>
                                            </div>
                                            <details className="font-normal text-gray-700">
                                              <summary className="cursor-pointer text-xs font-semibold text-indigo-700">
                                                {t('View pronunciation details')}
                                              </summary>
                                              <div className="mt-2 max-w-xl space-y-3 rounded border border-indigo-100 bg-indigo-50/40 p-3 text-xs leading-5">
                                                {event.pronunciation_feedback && (
                                                  <p data-i18n-skip>{event.pronunciation_feedback}</p>
                                                )}
                                                {Array.isArray(event.pronunciation_issues) && event.pronunciation_issues.length > 0 && (
                                                  <div className="space-y-2 border-l-2 border-indigo-200 pl-2">
                                                    {event.pronunciation_issues.map((issue, issueIndex) => (
                                                      <div key={`${issue.segment || 'issue'}-${issueIndex}`} data-i18n-skip>
                                                        {issue.segment && <div className="font-semibold text-gray-900">{issue.segment}</div>}
                                                        {issue.problem && <div>{issue.problem}</div>}
                                                        {issue.correction && <div className="text-indigo-700">{issue.correction}</div>}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                                {event.pronunciation_recording_id && (
                                                  <button
                                                    type="button"
                                                    onClick={() => playPronunciationRecording(event.pronunciation_recording_id)}
                                                    disabled={Boolean(recordingState.id)}
                                                    className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                                                  >
                                                    <SpeakerWaveIcon className="h-3.5 w-3.5" />
                                                    {t(recordingState.id === event.pronunciation_recording_id ? 'Playing recording...' : 'Play recording')}
                                                  </button>
                                                )}
                                              </div>
                                            </details>
                                          </div>
                                        ) : isAiHint ? (
                                          <details>
                                            <summary className="cursor-pointer font-medium text-indigo-700">
                                              {Number(event.turn || 1) === 1
                                                ? t('Initial guidance')
                                                : t('Follow-up {number}', { number: Math.max(1, Number(event.turn || 1) - 1) })}
                                            </summary>
                                            <div className="mt-2 max-w-xl space-y-2 text-xs leading-5 text-gray-600">
                                              {event.current_answer && (
                                                <div>
                                                  <span className="font-semibold">{t('Current arrangement')}:</span>{' '}
                                                  <span data-i18n-skip>{event.current_answer}</span>
                                                </div>
                                              )}
                                              {event.student_question && (
                                                <div>
                                                  <span className="font-semibold">{t('Student question')}:</span>{' '}
                                                  <span data-i18n-skip>{event.student_question}</span>
                                                </div>
                                              )}
                                              {event.assistant_reply && (
                                                <div>
                                                  <span className="font-semibold">{t('Coach reply')}:</span>{' '}
                                                  <span data-i18n-skip>{event.assistant_reply}</span>
                                                </div>
                                              )}
                                            </div>
                                          </details>
                                        ) : (
                                          event.correct ? t('Correct') : t('Retry')
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : <div className="border-y border-gray-200 py-8 text-center text-sm text-gray-500">No recorded practice activity on this date.</div>}
            {recordingState.error && <div role="alert" className="mt-3 text-sm text-rose-700">{recordingState.error}</div>}
          </div>
        </>
      )}
      </div>
    </section>
  );
};

export default DailyPracticeReportPanel;
